import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Globe2, Heart, Calendar, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useHostFavorites } from '@/hooks/use-host-favorites';
import { ExternalRatings } from '@/components/host/ratings/ExternalRatings';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Link } from 'react-router-dom';

interface Host {
  id: string;
  username: string | null;
  avatar_url?: string;
  languages?: string[];
  bio?: string;
  is_verified?: boolean;
  verification_date?: string;
  google_maps_rating?: number;
  google_maps_reviews_count?: number;
  trustpilot_rating?: number;
  trustpilot_reviews_count?: number;
  location?: string;
  specialties?: string[];
}

interface HostCardProps {
  host: Host;
  sessionUserId?: string;
}

export const HostCard = ({ host, sessionUserId }: HostCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  if (!host || !host.id) {
    console.error('Invalid host data:', host);
    return null;
  }
  
  const { isHostFollowed, followHost, unfollowHost } = useHostFavorites(sessionUserId);

  const { data: verificationData } = useQuery({
    queryKey: ['verification', host.id],
    enabled: !!host.id,
    queryFn: async () => {
      console.log('Fetching verification data for host:', host.id);
      const { data, error } = await supabase
        .from('verification_requests')
        .select('*')
        .eq('host_id', host.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching verification data:', error);
        return null;
      }
      console.log('Fetched verification data:', data);
      return data;
    },
  });

  const toggleFollow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!sessionUserId) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please log in to follow hosts",
      });
      return;
    }

    try {
      if (isHostFollowed(host.id)) {
        unfollowHost(host.id);
      } else {
        followHost(host.id);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  const navigateToTrips = (tab: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/trips?tab=${tab}`);
  };

  const handleViewProfile = () => {
    if (sessionUserId && sessionUserId === host.id) {
      navigate('/profile');
      return;
    }
    navigate(`/host/${host.id}`);
  };

  const isPendingVerification = verificationData?.status === 'pending';
  
  const hostProfileUrl = sessionUserId && sessionUserId === host.id ? '/profile' : `/host/${host.id}`;

  const displayName = host.username || 'Anonymous Host';
  const avatarInitial = (displayName[0] || 'A').toUpperCase();

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-4">
            <Avatar className="h-12 w-12 flex-shrink-0">
              <AvatarImage src={host.avatar_url} alt={displayName} />
              <AvatarFallback>{avatarInitial}</AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-1 min-w-0">
              <h3 className="font-semibold truncate">{displayName}</h3>
              <div className="overflow-hidden">
                <ExternalRatings
                  isVerified={host.is_verified}
                  verificationDate={host.verification_date}
                  googleRating={host.google_maps_rating}
                  googleReviewsCount={host.google_maps_reviews_count}
                  trustpilotRating={host.trustpilot_rating}
                  trustpilotReviewsCount={host.trustpilot_reviews_count}
                />
              </div>
              {isPendingVerification && (
                <span className="text-sm text-amber-600">Verification Pending</span>
              )}
              {host.location && (
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Globe2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span className="truncate">{host.location}</span>
                </div>
              )}
              {host.languages && host.languages.length > 0 && (
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Globe2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span className="truncate">{host.languages.join(', ')}</span>
                </div>
              )}
              {host.bio && (
                <p className="text-sm text-gray-500 line-clamp-2 break-words">{host.bio}</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFollow}
              className={isHostFollowed(host.id) ? 'text-primary' : ''}
              aria-label={isHostFollowed(host.id) ? "Remove from favorites" : "Add to favorites"}
              title={isHostFollowed(host.id) ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart 
                className="w-5 h-5" 
                fill={isHostFollowed(host.id) ? 'currentColor' : 'none'} 
                aria-hidden="true" 
              />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={navigateToTrips('hosts')}
              aria-label="View favorite hosts"
              title="View favorite hosts"
            >
              <Users className="w-5 h-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={navigateToTrips('upcoming')}
              aria-label="View upcoming sessions"
              title="View upcoming sessions"
            >
              <Calendar className="w-5 h-5" aria-hidden="true" />
            </Button>
            <Link 
              to={hostProfileUrl}
              onClick={(e) => {
                e.preventDefault();
                handleViewProfile();
              }}
            >
              <Button 
                size="sm"
                aria-label={`View profile of ${displayName}`}
              >
                View Profile
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
